import { Action, AnyAction } from 'redux';

type ChildReducer<S = any, A extends Action = AnyAction, PS = any> = (
  state: S | undefined,
  action: A,
  parentState: PS,
) => S;

type ChildReducersMapObject<S = any, A extends Action = Action> = {
  [K in keyof S]: ChildReducer<S[K], A, S>;
};

type StateFromChildReducersMapObject<M> = M extends ChildReducersMapObject<any, any>
  ? { [P in keyof M]: M[P] extends ChildReducer<infer S, any, any> ? S : never }
  : never;

/**
 * A modification of `combineReducers` which returns the parent state as a third
 * argument for any reducer.  This allows values from the parent state to be
 * easily accessible if needed.
 *
 * NOTE: The code structure is directly taken from `combineReducers` in redux,
 * but is simplified so will not show some redux-related errors:
 * https://github.com/reduxjs/redux/blob/master/src/combineReducers.ts.
 *
 * @template S Combined state object type.
 *
 * @param reducers An object whose values correspond to different reducer
 *   functions that need to be combined into one.  Each individual reducer now
 *   has access to a third argument containing the parent state.
 *   See https://github.com/reduxjs/redux/blob/master/src/combineReducers.ts for
 *   other rules/info regarding reducers.
 *
 * @returns A reducer function that invokes every reducer inside the passed
 *   object, and builds a state object with the same shape.
 *
 * @example
 * ```javascript
 * const descriptionReducer = (state = '', action, parentState) => {
 *   switch(action.type) {
 *     case 'RENDER_DESCRIPTION':
 *       // Uses value from parent when calculating state
 *       return `Item: ${parentState.enabled ? 'enabled' : 'disabled'}`;
 *     default:
 *       return state;
 *   }
 * }
 *
 * combineChildReducers({
 *   description: descriptionReducer,
 *   enabled: enabledReducer,
 * });
 * ```
 */
export default function combineChildReducers(reducers: ChildReducersMapObject) {
  const reducerKeys = Object.keys(reducers);
  const finalReducers: ChildReducersMapObject = {};
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < reducerKeys.length; i++) {
    const key = reducerKeys[i];

    if (process.env.NODE_ENV !== 'production') {
      if (typeof reducers[key] === 'undefined') {
        console.warn(`No reducer provided for key "${key}"`);
      }
    }

    if (typeof reducers[key] === 'function') {
      finalReducers[key] = reducers[key];
    }
  }
  const finalReducerKeys = Object.keys(finalReducers);

  return function combination(
    state: StateFromChildReducersMapObject<typeof reducers> = {},
    action: AnyAction,
  ) {
    let hasChanged = false;
    const nextState: StateFromChildReducersMapObject<typeof reducers> = {};
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < finalReducerKeys.length; i++) {
      const key = finalReducerKeys[i];
      const reducer = finalReducers[key];
      const previousStateForKey = state[key];
      const nextStateForKey = reducer(previousStateForKey, action, state);
      if (typeof nextStateForKey === 'undefined') {
        const actionType = action && action.type;
        throw new Error(
          `When called with an action of type ${
            actionType ? `"${String(actionType)}"` : '(unknown type)'
          }, the slice reducer for key "${key}" returned undefined. ` +
            `To ignore an action, you must explicitly return the previous state. ` +
            `If you want this reducer to hold no value, you can return null instead of undefined.`,
        );
      }
      nextState[key] = nextStateForKey;
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey;
    }
    hasChanged = hasChanged || finalReducerKeys.length !== Object.keys(state).length;
    return hasChanged ? nextState : state;
  };
}
